import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { currency } from "@/lib/status";
import { Search, Link2, Sparkles, Car, ChevronRight, Bookmark, Trash2, CheckCircle2, Clock, FileText, Landmark, Upload } from "lucide-react";
import { toast } from "sonner";
import { LastCallRibbon } from "@/components/LastCallRibbon";
import { PrequalCard } from "@/components/PrequalCard";

export default function BuyerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [requests, setRequests] = useState(null);
  const [details, setDetails] = useState({});
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    api.get("/requests").then(({ data }) => {
      setRequests(data);
      Promise.all(data.map((request) => api.get(`/requests/${request.id}`).then(({ data: detail }) => [request.id, detail]).catch(() => null)))
        .then((items) => setDetails(Object.fromEntries(items.filter(Boolean))));
    }).catch(() => setRequests([]));
    api.get("/saved-searches").then(({ data }) => setSaved(data)).catch(() => {});
  }, []);

  const removeSaved = async (id) => {
    try { await api.delete(`/saved-searches/${id}`); setSaved((s) => s.filter((x) => x.id !== id)); toast.success("Saved search removed"); }
    catch { toast.error("Could not remove"); }
  };

  const firstName = (user?.name || "").split(" ")[0] || "there";

  const actions = [
    { icon: Sparkles, label: "Tell us your dream car", path: "/concierge", testId: "garage-action-concierge", primary: true },
    { icon: Link2, label: "Paste a dealer link", path: "/request/new", testId: "garage-action-link" },
    { icon: Search, label: "Browse inventory", path: "/inventory", testId: "garage-action-browse" },
  ];

  const title = (r) => {
    if (r.source === "concierge") return "Dream-car search";
    return [r.year, r.make, r.model].filter(Boolean).join(" ") || "Vehicle request";
  };
  const subtitle = (r) => {
    if (r.source === "concierge") {
      const c = r.concierge || {};
      return [c.body_style, c.brands, c.budget].filter(Boolean).join(" · ") || "Broker sourcing your car";
    }
    return `${r.trim || ""}${r.color ? ` · ${r.color}` : ""} · ${r.source === "dealer_link" ? "Dealer link" : "Inventory"} · ${r.msrp ? currency(r.msrp) : "Quote pending"}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-50 sm:text-4xl" data-testid="garage-greeting">Welcome back, {firstName}.</h1>
          <p className="mt-2 text-slate-600 dark:text-slate-300">Here's every car we're working on for you — from verification to your driveway.</p>
        </div>

        <PrequalCard />

        <LastCallRibbon />

        {user && !user.legal_name && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="profile-nudge">
            <p className="text-sm text-amber-800">Complete your profile — add your full legal name and address so financing and closing paperwork are ready to go.</p>
            <Button onClick={() => navigate("/profile")} className="rounded-md bg-amber-600 font-semibold hover:bg-amber-700" data-testid="profile-nudge-btn">Complete profile</Button>
          </div>
        )}

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {actions.map((a) => (            <button key={a.path} data-testid={a.testId} onClick={() => navigate(a.path)}
              className={`group flex items-center gap-3 rounded-xl border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm ${a.primary ? "border-rose-200 bg-rose-50/50" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300"}`}>
              <div className={`flex h-10 w-10 items-center justify-center rounded-md ${a.primary ? "bg-slate-950 text-white" : "bg-rose-50 text-rose-600"}`}><a.icon className="h-5 w-5" strokeWidth={2.5} /></div>
              <span className="font-display text-sm font-bold text-slate-900 dark:text-slate-100">{a.label}</span>
              <ChevronRight className="ml-auto h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5" />
            </button>
          ))}
        </div>

        {saved.length > 0 && (
          <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="saved-searches">
            <div className="flex items-center gap-2"><Bookmark className="h-4 w-4 text-rose-600" /><p className="font-display text-sm font-bold text-slate-900 dark:text-slate-100">Saved searches</p></div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">We'll email you when a matching car lands in stock.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {saved.map((s) => (
                <div key={s.id} data-testid={`saved-search-${s.id}`} className="group flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 py-1 pl-3 pr-1.5 text-sm">
                  <button onClick={() => navigate(`/inventory?${s.query_string}`)} className="font-semibold text-slate-700 dark:text-slate-300 hover:text-rose-600" data-testid={`apply-saved-${s.id}`}>{s.name}</button>
                  <button onClick={() => removeSaved(s.id)} className="text-slate-400 dark:text-slate-500 hover:text-red-500" data-testid={`remove-saved-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {requests === null ? (
          <div className="mt-8 space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-lg" />)}</div>
        ) : requests.length === 0 ? (
          <div className="mt-8 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-10 text-center" data-testid="garage-empty">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600"><Car className="h-6 w-6" /></div>
            <p className="mt-4 font-display text-xl font-bold text-slate-900 dark:text-slate-100">Your garage is empty</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Pick a way to start and our California brokers handle the rest.</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <Button onClick={() => navigate("/concierge")} className="rounded-md bg-slate-950 font-semibold hover:bg-slate-800" data-testid="empty-concierge-btn"><Sparkles className="mr-1.5 h-4 w-4" /> Tell us your dream car</Button>
              <Button onClick={() => navigate("/request/new")} variant="outline" className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="empty-link-btn"><Link2 className="mr-1.5 h-4 w-4" /> Paste a link</Button>
              <Button onClick={() => navigate("/inventory")} variant="outline" className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="empty-browse-btn"><Search className="mr-1.5 h-4 w-4" /> Browse</Button>
            </div>
          </div>
        ) : (
          <div className="mt-8 space-y-3" data-testid="garage-deals-list">
            {requests.map((r) => (
              <div key={r.id} data-testid={`request-row-${r.id}`} onClick={() => navigate(`/requests/${r.id}`)}
                className="group cursor-pointer rounded-lg border border-slate-200 bg-white p-5 transition-colors hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-md ${r.source === "concierge" ? "bg-rose-50 text-rose-600" : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"}`}>
                    {r.source === "concierge" ? <Sparkles className="h-6 w-6" /> : <Car className="h-6 w-6" />}
                  </div>
                  <div>
                    <p className="font-display text-lg font-bold text-slate-900 dark:text-slate-100">{title(r)}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{subtitle(r)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <StatusBadge status={r.status} size="lg" />
                  <ChevronRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-0.5" />
                </div>
                </div>
                <DealApplicationSummary r={r} detail={details[r.id]} navigate={navigate} />
              </div>
            ))}
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}

function DealApplicationSummary({ r, detail, navigate }) {
  if (!detail) return <p className="mt-4 text-xs text-slate-400" data-testid={`application-loading-${r.id}`}>Loading application status…</p>;
  const prequal = detail.prequalification;
  const financing = detail.financing;
  const required = [
    "drivers_license_front",
    "drivers_license_back",
    "proof_of_income",
    ...(detail.financing_document_requirements?.require_residence ? ["residence"] : []),
  ];
  const uploaded = new Set((detail.documents || []).map((doc) => doc.doc_type));
  const missingDocs = required.filter((key) => !uploaded.has(key));
  const prequalState = !prequal ? "Missing information" : prequal.status === "prequalified" ? "Complete" : prequal.status === "pending" ? "Needs review" : "Needs attention";
  const financingState = !financing ? "Not started" : ["approved", "declined"].includes(financing.status) ? financing.status[0].toUpperCase() + financing.status.slice(1) : "Submitted";
  const next = prequalState !== "Complete" ? { label: "Review prequal", tab: "prequal", icon: FileText } : !financing ? { label: "Complete financing", tab: "financing", icon: Landmark } : missingDocs.length ? { label: "Upload required documents", tab: "documents", icon: Upload } : { label: "View application", tab: "financing", icon: CheckCircle2 };
  const NextIcon = next.icon;
  const rows = [["Prequal", prequalState], ["Financing", financingState], ["Documents", missingDocs.length ? `${missingDocs.length} required upload${missingDocs.length === 1 ? "" : "s"} remaining` : "Required uploads complete"]];
  return <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800" data-testid={`application-summary-${r.id}`}><div className="grid gap-2 sm:grid-cols-3">{rows.map(([label, value]) => <div key={label} className="flex items-center gap-2 text-xs"><Clock className="h-3.5 w-3.5 text-slate-400" /><span className="font-bold text-slate-700 dark:text-slate-200">{label}</span><span className="text-slate-500 dark:text-slate-400">{value}</span></div>)}</div><button onClick={(event) => { event.stopPropagation(); navigate(`/requests/${r.id}?tab=${next.tab}`); }} className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-rose-600 hover:text-rose-700" data-testid={`application-next-action-${r.id}`}><NextIcon className="h-4 w-4" />{next.label}<ChevronRight className="h-4 w-4" /></button></div>;
}
